import { useCallback, useEffect, useState } from "react"

import { dequal } from "dequal/lite"

import { useCurrentView } from "web/components"

import useDebouncedValue from "web/hooks/behaviour/useDebouncedValue"
import useSelectionBroadcast from "web/hooks/behaviour/useSelectionBroadcast"
import {
  useCurrentUser,
  useObjectList,
  useViewableInvalidation,
} from "web/hooks/data"

import { LocalizationContext } from "web/contexts"

import TableView from "./TableView/TableView"
import NoData from "web/components/ObjectList/NoData"
import useViewOrEditTopicModal from "web/components/ViewOrEdit/ViewOrEditTopic"

export function ObjectTable({
  subjectType,
  subjectId,
  parentSubjectType,
  parentSubjectId,
  fetchURL: fetchURLFromProps,
  exportPath,
  topic,
  modalTitle,
  enableScheduledExport = false,
  enableExport = true,
  isFavouriteEnabled = true,
  onItemClick,
  subjectBaseUrl,
  enableEdit = true,
  enableFieldSelection = true,
  onAdd,
  listTypeFriendlyName,
  enableSelection = false,
  splitView = false,
  searchQuery,
  setSearchQuery,
  selectedIds,
  setSelectedIds,
  onAvailableIdsChanged,
}) {
  const {
    view,
    page,
    setPage,
    viewLocalizationContextValue,
    handleSortChanged,
    handleRowsPerPageChanged,
    handleGroupChanged,
    handleFrozenFieldChanged,
  } = useCurrentView()

  const currentUser = useCurrentUser()

  useViewableInvalidation()

  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)

  const hasTopic = topic !== undefined

  const topicId = hasTopic ? topic.id : undefined

  const viewOrEditTopicModal = useViewOrEditTopicModal({
    subjectType,
    topic,
    title: modalTitle,
  })

  const canShowFavouriteColumn =
    isFavouriteEnabled && !hasTopic && subjectType !== "task"

  const fetchResult = useObjectList({
    subjectType,
    subjectId,
    parentSubjectType,
    parentSubjectId,
    searchQuery: debouncedSearchQuery,
    topic: topicId,
    view,
    url: fetchURLFromProps,
    page: page,
    includeLocationOrdinals: splitView,
    swrOptions: { dedupingInterval: 0, compare: dequal },
  })

  const {
    detail: data,
    group_totals: groupTotals,
    grand_totals: grandTotals,
    sticky_group_totals: stickyGroupTotals,
    sticky_grand_totals: stickyGrandTotals,
    group_collection: groupCollection,
    page: pageMeta,
  } = fetchResult

  const canAdd = typeof onAdd === "function"

  function openTopicRecord({ data: record }) {
    viewOrEditTopicModal({
      recording: record,
      isCrossSubject: false,
      subjectUrl: subjectBaseUrl && `${subjectBaseUrl}/${record.subject_id}`,
    })
  }

  const handleRowClick = onItemClick || (hasTopic ? openTopicRecord : undefined)

  function handleSelectAllChanged(selectAll) {
    if (selectAll) {
      setSelectedIds([...new Set([...selectedIds, ...data.map((el) => el.id)])])
    } else {
      setSelectedIds([])
    }
  }

  function handleSelectionChanged(value) {
    if (value.selected) {
      setSelectedIds([...selectedIds, value.id])
    } else {
      setSelectedIds(selectedIds.filter((el) => el !== value.id))
    }
  }

  if (enableSelection) {
    for (const datum of data) {
      datum.selected = selectedIds.includes(datum.id)
    }
  }

  useEffect(() => {
    onAvailableIdsChanged?.(data.map((datum) => datum.id))
  }, [data, onAvailableIdsChanged])

  function refreshObjectList(event) {
    data.refresh()

    if (event.detail?.clearSelection) {
      setSelectedIds([])
    }

    if (event.detail?.resetPage) {
      setPage(1)
    }
  }

  useEffect(() => {
    document.addEventListener(
      "event-dispatcher:refresh-object-list",
      refreshObjectList
    )
    return () =>
      document.removeEventListener(
        "event-dispatcher:refresh-object-list",
        refreshObjectList
      )
  })

  function resetPage() {
    setPage(1)
  }

  useEffect(() => {
    document.addEventListener(
      "event-dispatcher:viewable-invalidated",
      resetPage
    )
    return () =>
      document.removeEventListener(
        "event-dispatcher:viewable-invalidated",
        resetPage
      )
  })

  useEffect(() => {
    const eventName = data.isValidating
      ? "objectListLoading"
      : "objectListLoaded"
    document.dispatchEvent(new CustomEvent(eventName))
  }, [data.isValidating])

  function handlePageChanged(page) {
    setPage(page)
  }

  return (
    <LocalizationContext.Provider value={viewLocalizationContextValue}>
      <div
        id="object-list-body"
        className="flex min-h-0 grow flex-col overflow-hidden"
      >
        <TableView
          page={page}
          pageCount={pageMeta?.page_count}
          recordCount={pageMeta?.record_count}
          rowsPerPage={view.table.rows_per_page}
          parentSubjectType={parentSubjectType}
          parentSubjectId={parentSubjectId}
          onRowClick={handleRowClick}
          onSelectionChanged={enableSelection && handleSelectionChanged}
          onSelectAllChanged={enableSelection && handleSelectAllChanged}
          onSort={handleSortChanged}
          onGroup={handleGroupChanged}
          onRowsPerPageChanged={handleRowsPerPageChanged}
          onFrozenFieldChanged={handleFrozenFieldChanged}
          handlePageChanged={handlePageChanged}
          exportPath={exportPath}
          view={view}
          enableScheduledExport={
            enableScheduledExport && currentUser.is_super_user
          }
          enableExport={enableExport}
          enableEdit={enableEdit}
          enableFieldSelection={enableFieldSelection}
          enableFavourites={canShowFavouriteColumn}
          groupTotals={groupTotals}
          grandTotals={grandTotals}
          stickyGroupTotals={stickyGroupTotals}
          stickyGrandTotals={stickyGrandTotals}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showRowNumbers={splitView}
          topic={topic}
          data={data}
          groupCollection={groupCollection}
          fields={view.fields}
          subjectType={subjectType}
          subjectId={subjectId}
          emptyState={
            <NoData
              handleAddItem={canAdd && (() => onAdd(data))}
              listTypeFriendlyName={listTypeFriendlyName}
            />
          }
        />
      </div>
    </LocalizationContext.Provider>
  )
}

export default function StandaloneObjectTable({
  enableSelection = false,
  ...props
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [availableIds, setAvailableIds] = useState([])

  const handleAvailableIdsChanged = useCallback((ids) => {
    setAvailableIds((previous) => (dequal(previous, ids) ? previous : ids))
  }, [])

  const visibleSelectedIds = selectedIds.filter((id) =>
    availableIds.includes(id)
  )

  useSelectionBroadcast({ enableSelection, selectedIds: visibleSelectedIds })

  return (
    <ObjectTable
      {...props}
      enableSelection={enableSelection}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      onAvailableIdsChanged={handleAvailableIdsChanged}
    />
  )
}
